import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';
import { SocketProvider } from './utils/socket';

// Components (loaded immediately - needed for app structure)
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedRedirect from './components/RoleBasedRedirect';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';

// Loading Component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    fontSize: '1.2rem',
    color: '#4CAF50'
  }}>
    Loading...
  </div>
);

// Lazy-loaded Auth Pages
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const OwnerLogin = lazy(() => import('./pages/owner/OwnerLogin'));
const UserOTPLogin = lazy(() => import('./pages/user/UserOTPLogin'));

// Lazy-loaded Admin Pages
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));

// Lazy-loaded Owner Pages
const OwnerDashboard = lazy(() => import('./pages/owner/OwnerDashboard'));
const OwnerNotifications = lazy(() => import('./pages/owner/OwnerNotifications'));
const OwnerBookings = lazy(() => import('./pages/owner/OwnerBookings'));
const OwnerProfile = lazy(() => import('./pages/owner/OwnerProfile'));
const OwnerReports = lazy(() => import('./pages/owner/OwnerReports'));
const OwnerPayouts = lazy(() => import('./pages/owner/OwnerPayouts'));
const OwnerSlotBlocking = lazy(() => import('./pages/owner/OwnerSlotBlocking'));
const OwnerPromoteTurf = lazy(() => import('./pages/owner/OwnerPromoteTurf'));
const EditProfile = lazy(() => import('./pages/owner/EditProfile'));
const TestOwner = lazy(() => import('./pages/owner/TestOwner'));
const AboutUs = lazy(() => import('./pages/owner/footer/AboutUs'));
const HelpCenter = lazy(() => import('./pages/owner/footer/HelpCenter'));
const FAQs = lazy(() => import('./pages/owner/footer/FAQs'));
const ContactSupport = lazy(() => import('./pages/owner/footer/ContactSupport'));
const CancellationPolicy = lazy(() => import('./pages/owner/footer/CancellationPolicy'));
const TermsOfService = lazy(() => import('./pages/owner/footer/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/owner/footer/PrivacyPolicy'));
const CookiePolicy = lazy(() => import('./pages/owner/footer/CookiePolicy'));

// Lazy-loaded User Pages
const Home = lazy(() => import('./pages/user/Home'));
const ResponsiveShowcase = lazy(() => import('./pages/user/ResponsiveShowcase'));
const BookingFilter = lazy(() => import('./pages/user/BookingFilter'));
const SearchTurfs = lazy(() => import('./pages/user/SearchTurfs'));
const NearbyTurfs = lazy(() => import('./pages/user/NearbyTurfs'));
const TurfDetails = lazy(() => import('./pages/user/TurfDetails'));
const QuickTurfDetails = lazy(() => import('./pages/user/QuickTurfDetails'));
const MyBookings = lazy(() => import('./pages/user/MyBookings'));
const AboutTurfNow = lazy(() => import('./pages/user/footer/AboutTurfNow'));
const ContactUs = lazy(() => import('./pages/user/footer/ContactUs'));
const ListYourTurf = lazy(() => import('./pages/user/footer/ListYourTurf'));
const UserFAQs = lazy(() => import('./pages/user/footer/FAQs'));
const UserCancellationPolicy = lazy(() => import('./pages/user/footer/CancellationPolicy'));
const LegalPolicy = lazy(() => import('./pages/user/footer/LegalPolicy'));
const UserTermsOfService = lazy(() => import('./pages/user/footer/TermsOfService'));

const ResponsiveIndex = () => {
  return (
    <Layout>
      <RoleBasedRedirect>
        <Home />
      </RoleBasedRedirect>
    </Layout>
  );
};

function App() {
  // const [msg, setMsg] = useState("");

  // useEffect(() => {
  //   fetch(`${process.env.REACT_APP_API_URL}/api/hello`)
  //     .then(res => res.json())
  //     .then(data => setMsg(data.message))
  //     .catch(err => console.error(err));
  // }, []);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SocketProvider>
        <Router>
          <ScrollToTop />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Full-screen routes — NO navbar/layout wrapper */}
              <Route path="/" element={<ResponsiveIndex />} />
              <Route path="/landing" element={<ResponsiveIndex />} />

              {/* All other routes — wrapped in Layout (navbar + footer) */}
              <Route path="/*" element={
                <Layout>
                  <RoleBasedRedirect>
                    <Routes>
                      {/* Public Routes */}
                      <Route path="/book" element={<BookingFilter />} />
                      <Route path="/responsive-showcase" element={<ResponsiveShowcase />} />
                      <Route path="/search" element={<SearchTurfs />} />
                      <Route path="/nearby" element={<NearbyTurfs />} />
                      <Route path="/quick-turf/:id" element={<QuickTurfDetails />} />
                      <Route path="/turf/:id" element={<TurfDetails />} />
                      <Route path="/about-turfnow" element={<AboutTurfNow />} />
                      <Route path="/contact-us" element={<ContactUs />} />
                      <Route path="/list-your-turf" element={<ListYourTurf />} />
                      <Route path="/faqs" element={<UserFAQs />} />
                      <Route path="/cancellation-policy" element={<UserCancellationPolicy />} />
                      <Route path="/legal-policy" element={<LegalPolicy />} />
                      <Route path="/terms-of-service" element={<UserTermsOfService />} />

                      {/* Auth Routes */}
                      <Route path="/admin/login" element={<AdminLogin />} />
                      <Route path="/owner/login" element={<OwnerLogin />} />
                      {/* <Route path="/owner/*" element={<OwnerDashboard />} /> */}
                      <Route path="/user/login" element={<UserOTPLogin />} />

                      {/* Protected Admin Routes */}
                      <Route
                        path="/admin/*"
                        element={
                          <ProtectedRoute role="admin">
                            <AdminDashboard />
                          </ProtectedRoute>
                        }
                      />

                      {/* Test Route */}
                      <Route path="/owner-test" element={<TestOwner />} />

                      {/* Protected Owner Routes */}
                      <Route
                        path="/owner/reports"
                        element={
                          <ProtectedRoute role="owner">
                            <OwnerReports />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner/payouts"
                        element={
                          <ProtectedRoute role="owner">
                            <OwnerPayouts />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner/slots"
                        element={
                          <ProtectedRoute role="owner">
                            <OwnerSlotBlocking />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner/promote"
                        element={
                          <ProtectedRoute role="owner">
                            <OwnerPromoteTurf />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner/notifications"
                        element={
                          <ProtectedRoute role="owner">
                            <OwnerNotifications />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner/profile/edit"
                        element={
                          <ProtectedRoute role="owner">
                            <EditProfile />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner/profile"
                        element={
                          <ProtectedRoute role="owner">
                            <OwnerProfile />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner/bookings"
                        element={
                          <ProtectedRoute role="owner">
                            <OwnerBookings />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner/about-us"
                        element={
                          <ProtectedRoute role="owner">
                            <AboutUs />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner/help-center"
                        element={
                          <ProtectedRoute role="owner">
                            <HelpCenter />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner/faqs"
                        element={
                          <ProtectedRoute role="owner">
                            <FAQs />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner/contact-support"
                        element={
                          <ProtectedRoute role="owner">
                            <ContactSupport />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner/cancellation-policy"
                        element={
                          <ProtectedRoute role="owner">
                            <CancellationPolicy />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner/terms"
                        element={
                          <ProtectedRoute role="owner">
                            <TermsOfService />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner/privacy"
                        element={
                          <ProtectedRoute role="owner">
                            <PrivacyPolicy />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner/cookies"
                        element={
                          <ProtectedRoute role="owner">
                            <CookiePolicy />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/owner"
                        element={
                          <ProtectedRoute role="owner">
                            <OwnerDashboard />
                          </ProtectedRoute>
                        }
                      />



                      {/* Protected User Routes */}
                      <Route
                        path="/user/bookings"
                        element={
                          <ProtectedRoute allowedRoles={['user', 'owner', 'admin']}>
                            <MyBookings />
                          </ProtectedRoute>
                        }
                      />

                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </RoleBasedRedirect>
                </Layout>
              } />
            </Routes>
          </Suspense>
          <Toaster position="top-center" />
        </Router>
      </SocketProvider>
    </ThemeProvider>
  );
}

export default App;
